/**
 * The value columns of a proposal on a portfolio in quantities, in read and edit mode alike: each of
 * weight, quantity and market value as the proposal holds it, as the portfolio held it before the
 * proposal ("… at <date>") and the delta between the two, plus the proposal's market price. Every
 * previous and delta cell reads the live row against the previous position the enhanced composition
 * carries, so the deltas follow editor edits and the previous columns never move.
 *
 * Where the backend sends no value a previous cell reads "N/A" with an info icon saying why, never 0;
 * its delta cell and the totals stay blank.
 */
import type { Currencies } from "@eu.mdotm.api/sphere";
import { DeltaIcon, InfoDelta } from "$root/components/InfoDelta";
import { user$ } from "$root/functional-areas/user";
import { formatDate, formatNumber, formatNumberWithBankersRounding } from "$root/localization/formatters";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import { sumArrayLike } from "$root/utils";
import { currencyCode } from "$root/utils/currency";
import { Row } from "@mdotm/shadcn/legacy/components";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import type { ColDef, IAggFuncParams, IRowNode, ValueFormatterParams } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { t } from "i18next";
import type { Instrument } from "../const";
import { isPendingConversion } from "./gate";
import { withNotAvailableCell } from "./not-available/withNotAvailableCell";
import type { PreviousPosition } from "./previous-position";

/** What the proposal columns read off a row. */
type ProposalRow = PreviousPosition & {
	quantity?: number;
	marketValue?: number;
	price?: number;
	weight?: number;
	previousWeight?: number;
	deleted?: boolean;
	isCash?: boolean;
	heldByValue?: boolean;
	unpriced?: boolean;
	newInstrumentCheck?: unknown;
};

/**
 * A non-cash row sized in money rather than shares. The editor flags it; the read view does not, and
 * there such a row is the one that comes without a quantity.
 */
function heldByValue(row: ProposalRow): boolean {
	return !row.isCash && (Boolean(row.heldByValue) || row.quantity == null);
}

/**
 * Quantity − previous quantity. Empty on the cash row and on a row held by value, which hold no
 * shares. A deleted row holds nothing, so its delta is the whole previous quantity sold.
 */
export function deltaQuantity(row: ProposalRow | undefined): number | undefined {
	if (!row || row.isCash || heldByValue(row) || row.previousQuantity == null) {
		return undefined;
	}
	return (row.deleted ? 0 : (row.quantity ?? 0)) - row.previousQuantity;
}

/**
 * Market value − previous market value. Empty on a row unpriced at the market date, whose value
 * there is unknown. A deleted row is worth nothing, as in the Market value column.
 */
export function deltaMarketValue(row: ProposalRow | undefined): number | undefined {
	if (!row || row.unpriced || row.previousMarketValue == null) {
		return undefined;
	}
	return (row.deleted ? 0 : (row.marketValue ?? 0)) - row.previousMarketValue;
}

/** The weight a row holds: a deleted row keeps its weight so a restore can bring it back, but holds nothing. */
function effectiveWeight(row: ProposalRow): number {
	return row.deleted ? 0 : (row.weight ?? 0);
}

/**
 * Weight − previous weight, in percentage points. A deleted row holds nothing, so its delta is the
 * whole previous weight sold.
 */
export function deltaWeight(row: ProposalRow | undefined): number | undefined {
	if (!row) {
		return undefined;
	}
	return effectiveWeight(row) - (row.previousWeight ?? 0);
}

/** The tooltip of an N/A cell: why the backend sent no value for it. */
type NotAvailableReason =
	| "TABLE.QUANTITY_PROPOSAL.NA.NO_MARKET_PRICE"
	| "TABLE.QUANTITY_PROPOSAL.NA.PREVIOUS_NOT_SENT"
	| "TABLE.QUANTITY_PROPOSAL.NA.CASH_NO_QUANTITY"
	| "TABLE.QUANTITY_PROPOSAL.NA.HELD_BY_VALUE"
	| "TABLE.QUANTITY.NA.HELD_BY_VALUE_PRICE";

/** Why a Quantity at cell has no value: the tooltip key, or none when it has one. */
function missingShareReason(row: ProposalRow): NotAvailableReason | undefined {
	if (row.isCash) {
		return "TABLE.QUANTITY_PROPOSAL.NA.CASH_NO_QUANTITY";
	}
	if (heldByValue(row)) {
		return "TABLE.QUANTITY_PROPOSAL.NA.HELD_BY_VALUE";
	}
	if (row.previousQuantity == null) {
		return "TABLE.QUANTITY_PROPOSAL.NA.PREVIOUS_NOT_SENT";
	}
	return undefined;
}

/** Why a Market value at cell has no value, as {@link missingShareReason}. */
function missingMoneyReason(row: ProposalRow): NotAvailableReason | undefined {
	return row.previousMarketValue == null ? "TABLE.QUANTITY_PROPOSAL.NA.PREVIOUS_NOT_SENT" : undefined;
}

/**
 * Why the market price is missing: a row held by value has none, and neither has a row the backend
 * sends no price for. A row added in this edit, or unpriced at the market date, is not one of them:
 * the editor shows its own state there — the check under way, the price on its way, or no price at
 * that day.
 */
function missingPriceReason(row: ProposalRow): NotAvailableReason | undefined {
	if (row.isCash || row.price != null) {
		return undefined;
	}
	if (heldByValue(row)) {
		return "TABLE.QUANTITY.NA.HELD_BY_VALUE_PRICE";
	}
	if (row.unpriced || row.newInstrumentCheck != null) {
		return undefined;
	}
	return "TABLE.QUANTITY_PROPOSAL.NA.NO_MARKET_PRICE";
}

/**
 * The N/A half of a column: on an instrument row `reason` names, the cell reads "N/A" with an info
 * icon saying why. Group and total rows carry no data and are left to the column's own formatter.
 */
function withNotAvailable(column: ColDef<Instrument>, reason: (row: Instrument) => NotAvailableReason | undefined) {
	return withNotAvailableCell(column, (row) => {
		const key = reason(row);
		return key ? t(key) : undefined;
	});
}

function formatShares(params: ValueFormatterParams): string {
	return params.value == null ? "" : formatNumber(params.value, 0);
}

/**
 * A delta cell: blank where there is no delta — no previous value to compare against (the previous
 * column beside it says why), or nothing at the precision it shows, a subtraction artefact
 * included — and `format` otherwise. Delta Weight's cell does the same.
 */
function formatDelta(
	format: (params: ValueFormatterParams) => string,
	decimals: number,
): (params: ValueFormatterParams) => string {
	return (params) => (params.value != null && Math.round(params.value * 10 ** decimals) === 0 ? "" : format(params));
}

/**
 * A delta cell's text with the arrow Delta Weight shows, up and signed "+" or down. A cell its
 * formatter leaves blank gets no arrow: there is no move to point at.
 */
function DeltaCell({ value, valueFormatted }: CustomCellRendererProps<Instrument>): React.ReactNode {
	const text = valueFormatted ?? "";
	if (text === "" || typeof value !== "number" || value === 0) {
		return text;
	}
	const increased = value > 0;
	return (
		<Row className="h-full w-full" justifyContent="end" alignItems="center" gap={2}>
			<DeltaIcon type={increased ? "increased" : "reduced"} />
			<span>{increased ? `+${text}` : text}</span>
		</Row>
	);
}

function leavesOf<T>(node: IRowNode<T>): Array<IRowNode<T>> {
	return node.allLeafChildren ?? [];
}

/**
 * Market value at <date> over a group or the grand total, cash included. Empty when any row under it
 * lacks a previous value — cash in a currency with no stored value, typically: a partial sum would
 * read as a real total.
 */
function sumPreviousMarketValues<T extends ProposalRow>(params: IAggFuncParams<T>): number | undefined {
	let sum = 0;
	for (const leaf of leavesOf(params.rowNode)) {
		const previous = leaf.data?.previousMarketValue;
		if (previous == null) {
			return undefined;
		}
		sum += previous;
	}
	return sum;
}

/**
 * Delta Market value over a group or the grand total: the instruments' deltas only. Cash is left out,
 * so the total reads as the money moved into or out of instruments. Empty where no row has a delta.
 */
function sumInstrumentDeltas<T extends ProposalRow>(params: IAggFuncParams<T>): number | undefined {
	let sum: number | undefined;
	for (const leaf of leavesOf(params.rowNode)) {
		const delta = leaf.data?.isCash ? undefined : deltaMarketValue(leaf.data);
		if (delta != null) {
			sum = (sum ?? 0) + delta;
		}
	}
	return sum;
}

/** Delta Weight over a group: Σ weight − Σ previous weight, a deleted row counted as holding nothing. */
function groupDeltaWeight<T extends ProposalRow>(node: IRowNode<T>): number {
	const leaves = leavesOf(node);
	const weight = sumArrayLike(leaves, (leaf) => (leaf.data ? effectiveWeight(leaf.data) : 0));
	return weight - sumArrayLike(leaves, (leaf) => leaf.data?.previousWeight ?? 0);
}

const dimDeleted = {
	"opacity-50": (params: { node: IRowNode<ProposalRow> }) => Boolean(params.node.data?.deleted),
};

/** Wraps the header of a previous column, whose date makes it the longest in the grid. */
const wrappedHeader = { wrapHeaderText: true, autoHeaderHeight: true } satisfies ColDef;

/**
 * Delta Weight, rendered like the weights proposal's Delta column — a row sold down to nothing, a
 * deleted one included, reads "Removed" — but counting a deleted row as holding nothing, on its own
 * cell and in its group's sum. Blank where the delta is nothing at the precision it shows, as the
 * other delta cells are.
 */
function deltaWeightColumn(): ColDef<Instrument> {
	return {
		headerName: t("TABLE.HEADERS.DELTA_WEIGHT"),
		colId: "DELTA_WEIGHT",
		editable: false,
		valueGetter: (params) => (params.node?.group ? groupDeltaWeight(params.node) : deltaWeight(params.data)),
		cellRenderer(params: CustomCellRendererProps<Instrument>) {
			// InfoDelta's own default where the user has no preference yet.
			const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences ?? 2;
			const movesAtPrecision = (delta: number) => Math.round(delta * 10 ** decimalPlaces) !== 0;
			if (params.node.group) {
				const groupDelta = groupDeltaWeight(params.node);
				if (!movesAtPrecision(groupDelta)) {
					return "";
				}
				const enh = sumArrayLike(leavesOf(params.node), (leaf) => (leaf.data ? effectiveWeight(leaf.data) : 0));
				return (
					<Row className="h-full w-full" alignItems="center">
						<InfoDelta diff={groupDelta} enh={enh} decimalPlaces={decimalPlaces} />
					</Row>
				);
			}
			const delta = deltaWeight(params.data);
			if (params.data == null || delta == null || !movesAtPrecision(delta)) {
				return "";
			}
			return (
				<Row className="h-full w-full" alignItems="center">
					<InfoDelta diff={delta} enh={effectiveWeight(params.data)} decimalPlaces={decimalPlaces} />
				</Row>
			);
		},
		tooltipValueGetter(params) {
			const delta = deltaWeight(params.data);
			if (delta == null) {
				return null;
			}
			// A difference: full precision would only expose double-subtraction artifacts, so it rounds like the cell.
			return `${formatNumberWithBankersRounding(delta, readAtom(user$).userDecimalPlacesPreferences)}%`;
		},
		cellClassRules: {
			...dimDeleted,
			"font-semibold": (params) => Boolean(params.node.group),
		},
		minWidth: 150,
	};
}

/** Locks a column in the right-pinned block, where the quantity comparison stays in view. */
const pinnedRight = { pinned: "right", lockPinned: true, lockPosition: false, lockVisible: true } satisfies ColDef;

/**
 * The ten value columns of a quantity proposal, in grid order: Weight, Weight at, Delta Weight, Market
 * value at and Delta Market value unpinned, then Quantity, Quantity at, Delta Quantity, Market price
 * and Market value pinned right. `proposalDate` (the portfolio's last modification) labels the
 * previous columns; `currency` is the AUM budget's and labels the market-value headers.
 *
 * A pending Convert-to-quantities proposal gets no Quantity at, Delta Quantity, Market value at or
 * Delta Market value: its previous position is the weights composition it converts, which held no
 * shares. Omitted rather than hidden, since a persisted column state would show a hidden column again.
 */
export function quantityProposalColumns(
	mode: "read" | "edit",
	extra?: {
		proposalDate?: string;
		currency?: Currencies | null;
		summary?: Parameters<typeof isPendingConversion>[0];
	},
): Array<ColDef<Instrument>> {
	const date = extra?.proposalDate ? formatDate(extra.proposalDate, { omitYear: true }) : "";
	const currency = currencyCode(extra?.currency);
	const comparesPositions = !(extra?.summary && isPendingConversion(extra.summary));

	const marketValue = sphereColumnsDef.instrumentMarketValue(
		{ currency: extra?.currency },
		{ editable: mode === "edit", ...pinnedRight },
	);
	// The Market value column's own formatter, so the previous and delta totals agree with its total to the cent.
	const formatMoney = (params: ValueFormatterParams<Instrument>): string =>
		typeof marketValue.valueFormatter === "function" ? marketValue.valueFormatter(params) : "";

	const previousWeight = sphereColumnsDef.percentage<Instrument>("previousWeight", {
		headerName: t("TABLE.HEADERS.WEIGHT_AT", { date }),
		colId: "PREVIOUS_WEIGHT",
		cellClassRules: dimDeleted,
		tooltipValueGetter: (params) =>
			typeof params.value === "number"
				? `${formatNumber(params.value, { maxDecimalPlaces: 16, minDecimalPlaces: 0 })}%`
				: null,
		valueFormatter(params) {
			const decimalPlaces = readAtom(user$).userDecimalPlacesPreferences;
			return `${formatNumberWithBankersRounding(params.value ?? 0, decimalPlaces)}%`;
		},
		aggFunc: "sum",
		minWidth: 150,
		...wrappedHeader,
	});

	const previousMarketValue = withNotAvailable(
		{
			headerName: t("TABLE.HEADERS.MARKET_VALUE_AT", { date, currency }),
			colId: "PREVIOUS_MARKET_VALUE",
			editable: false,
			cellDataType: "floating",
			aggFunc: sumPreviousMarketValues,
			valueGetter: (params) => params.data?.previousMarketValue,
			valueFormatter: formatMoney,
			cellClassRules: {
				...dimDeleted,
				"font-semibold": (params) => Boolean(params.node.group),
				"ag-cell--monospace-font": () => true,
			},
			minWidth: 150,
			...wrappedHeader,
		},
		missingMoneyReason,
	);

	const deltaMarketValueColumn: ColDef<Instrument> = {
		headerName: t("TABLE.HEADERS.DELTA_MARKET_VALUE", { currency }),
		colId: "DELTA_MARKET_VALUE",
		editable: false,
		cellDataType: "floating",
		aggFunc: sumInstrumentDeltas,
		valueGetter: (params) => deltaMarketValue(params.data),
		valueFormatter: formatDelta(formatMoney, 2),
		cellRenderer: DeltaCell,
		cellClassRules: {
			...dimDeleted,
			"font-semibold": (params) => Boolean(params.node.group),
			"ag-cell--monospace-font": () => true,
		},
		minWidth: 150,
	};

	const previousQuantity = withNotAvailable(
		{
			headerName: t("TABLE.HEADERS.QUANTITY_AT", { date }),
			colId: "PREVIOUS_QUANTITY",
			editable: false,
			cellDataType: "integer",
			valueGetter: (params) =>
				params.data && !missingShareReason(params.data) ? params.data.previousQuantity : undefined,
			valueFormatter: formatShares,
			cellClassRules: { ...dimDeleted, "ag-cell--monospace-font": () => true },
			minWidth: 120,
			...pinnedRight,
			...wrappedHeader,
		},
		missingShareReason,
	);

	const deltaQuantityColumn: ColDef<Instrument> = {
		headerName: t("TABLE.HEADERS.DELTA_QUANTITY"),
		colId: "DELTA_QUANTITY",
		editable: false,
		cellDataType: "integer",
		valueGetter: (params) => deltaQuantity(params.data),
		valueFormatter: formatDelta(formatShares, 0),
		cellRenderer: DeltaCell,
		cellClassRules: { ...dimDeleted, "ag-cell--monospace-font": () => true },
		minWidth: 120,
		...pinnedRight,
	};

	return [
		// Declared read-only: in edit mode `withQuantityCommitHandlers` enables weight edits.
		sphereColumnsDef.instrumentCompositionWeight({ initialWidth: 132, editable: false, minWidth: 132 }),
		previousWeight,
		deltaWeightColumn(),
		...(comparesPositions ? [previousMarketValue, deltaMarketValueColumn] : []),
		sphereColumnsDef.instrumentQuantity({ editable: mode === "edit", ...pinnedRight }),
		...(comparesPositions ? [previousQuantity, deltaQuantityColumn] : []),
		withNotAvailable(sphereColumnsDef.instrumentMarketPrice(pinnedRight), missingPriceReason),
		marketValue,
	];
}

/**
 * The composition export's column labels on a quantity proposal, by colId: the table's own headers,
 * so a downloaded file reads like the grid it came from. Only the columns the export carries are
 * relabelled; the rest have no concept and never reach it.
 */
export function quantityProposalExportLabels(
	proposalDate: string | undefined,
	currency: Currencies | null | undefined,
): Record<string, string> {
	return Object.fromEntries(
		quantityProposalColumns("read", { proposalDate, currency }).flatMap((col) =>
			col.colId && typeof col.headerName === "string" ? [[col.colId, col.headerName]] : [],
		),
	);
}
